import {
    AttachmentFilter,
    DownloadAttachmentResult,
    IAttachment,
    IAttachmentThumbnail,
    IAttachmentExtendedRecord,
    MergeDocumentsRequest,
    ConvertDocxDocumentsToPDFRequest,
    MergeDocumentsResult,
    SupersedeAttachmentResult,
    AttachmentsPageRequest,
    CreateAttachmentFromDocxRequest,
    CreateAttachmentFromDocxResult,
    CreateAttachmentFromTemplateRequest,
    DeleteAttachmentRequest,
    SplitAttachmentResult,
    AttachmentsPageResult,
} from "@shared/mirage/core/contracts/IAttachment";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import { BaseApiService } from "./BaseApiService";
import { IErrorMessage } from "@shared/mirage/core/contracts/IErrorMessage";
import { Email, SendEmailsOptions, SendEmailsRequest } from "@shared/mirage/core/contracts/Email";

import { SupersedeOptions, SupersedeAttachmentRequest, SupersedeAttachmentWithFileRequest, BulkInsertResult, BulkInsertRequest } from "@shared/mirage/services/AttachmentsService";
import { FieldValidationResult } from "@shared/mirage/core/contracts/ValidationResult";

export default class LocalAttachmentsService extends BaseApiService {
    constructor(publicOnly = true) {
        super(publicOnly ? "mirage/attachments" : "mirage/internalattachments");
    }
    public async ValidateAttachments(a: IAttachment[]): Promise<FieldValidationResult[]> {
        const response = await this.httpInstance().post(`/validate/`, a);
        return response.data;
    }

    public async convertDocxDocumentsToPdf(req: ConvertDocxDocumentsToPDFRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().post(`/convertDocxDocumentsToPdf`, req);
        return response.data;
    }

    public async createMergedDocument(req: MergeDocumentsRequest): Promise<MergeDocumentsResult> {
        const response = await this.httpInstance().post(`/createMergedDocument`, req);
        return response.data;
    }

    public async insertAttachments(attachments: IAttachment[], validate: boolean): Promise<BulkInsertResult> {
        const bulkInsertRequest: BulkInsertRequest = {
            attachments,
            validate,
        };
        const response = await this.httpInstance().post("/insertAttachments/", bulkInsertRequest);
        return response.data;
    }
    public async SplitAttachment(attachmentId: number): Promise<SplitAttachmentResult> {
        const response = await this.httpInstance().post(`/split/${attachmentId}`);
        return response.data;
    }
    public async Supersede(sourceId: number, existingId: number, options?: SupersedeOptions): Promise<SupersedeAttachmentResult> {
        const supersedeRequest: SupersedeAttachmentRequest = {
            sourceId,
            existingId,
            options,
        };
        const response = await this.httpInstance().post("/supersede", supersedeRequest);
        return response.data;
    }
    public async SupersedeWithFile(sourceId: number, uploadedFile: { base64: string; fileName: any; entityName: string; recordId: number }): Promise<SupersedeAttachmentResult> {
        const request: SupersedeAttachmentWithFileRequest = {
            sourceId,
            base64: uploadedFile.base64,
            fileName: uploadedFile.fileName,
            entityName: uploadedFile.entityName,
            recordId: uploadedFile.recordId,
        };

        const response = await this.httpInstance().post("/supersedeByFile", request);
        return response.data;
    }
    async CreateAttachmentFromDocx(request: CreateAttachmentFromDocxRequest): Promise<CreateAttachmentFromDocxResult> {
        const response = await this.httpInstance().post("/fromDocx", request);
        return response.data;
    }
    public async UpdateAttachments(attachments: IAttachment[]): Promise<boolean> {
        const response = await this.httpInstance().put("/", attachments);
        return response.status === 200;
    }
    public async GetAttachmentById(id: number): Promise<IAttachment> {
        const response = await this.httpInstance().get("/" + id);
        return response.data;
    }
    public async GetAttachments(entityName: string, recordId: number, filter: AttachmentFilter | null = null): Promise<IAttachmentExtendedRecord[]> {
        const response = await this.httpInstance().post(`/${entityName}/${recordId}`, filter);
        return response.data.attachments;
    }

    public async GetAttachmentsGrid(entityName: string, recordId: number, pageRequest: IEntityPageRequest): Promise<AttachmentsPageResult> {
        const response = await this.httpInstance().post("/grid/" + entityName + "/" + recordId, pageRequest);
        return response.data;
    }
    public async GetAttachmentsGridByIds(attachmentIds: number[], pageRequest: IEntityPageRequest): Promise<IEntityPageResult<IAttachmentThumbnail & IAttachmentExtendedRecord>> {
        const attachmentPageRequest = {
            ...pageRequest,
            attachmentIds,
        } as AttachmentsPageRequest;
        const response = await this.httpInstance().post("/grid/", attachmentPageRequest);
        return response.data;
    }
    public async DownloadAttachment(id: number): Promise<DownloadAttachmentResult | null> {
        const resp = await this.httpInstance().get("/download/" + id);
        if (resp.status === 404) {
            return null;
        }
        return resp.data;
    }
    public async DownloadAttachmentBody(id: number): Promise<string | null> {
        const resp = await this.httpInstance().get("/downloadBody/" + id);
        if (resp.status === 404) {
            return null;
        }
        return resp.data;
    }
    public async CreateAttachment(attachment: IAttachment): Promise<number | null> {
        const response = await this.httpInstance().post("/", attachment);
        return response.data;
    }
    public async SendEmails(emails: Email[], sendOptions?: SendEmailsOptions): Promise<IErrorMessage> {
        const sendEmailsRequest: SendEmailsRequest = {
            emails: emails,
            options: sendOptions,
        };
        const response = await this.httpInstance().post("/emails/", sendEmailsRequest);
        return response.data;
    }
    public async CreateAttachmentFromTemplate(request: CreateAttachmentFromTemplateRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().post("/fromTemplate/", request);
        return response.data;
    }
    public async DeleteAttachment(request: DeleteAttachmentRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().post("/delete", request);
        return response.data;
    }
}
